import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Link as RouterLink } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ClientsList from './components/ClientsList';
import NewEntry from './components/NewEntry';
import VisitsList from './components/VisitsList';
import Transportation from './components/Transportation';
import UnusualIncidentsList from './components/UnusualIncidentsList';
import Profile from './components/Profile';
import Billing from './components/Billing';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SessionTimeoutNotification from './components/SessionTimeoutNotification';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2D4B73', // Dark Blue for headers, buttons, fonts, text box outlines, footer icons
      light: '#4A6B94',
      dark: '#1E3452',
      contrastText: '#ffffff', // White text for better contrast on dark blue
    },
    secondary: {
      main: '#2D4B73', // Dark Blue accent
      light: '#4A6B94',
      dark: '#1E3452',
      contrastText: '#ffffff', // White text for better contrast on dark blue
    },
    background: {
      default: '#BFD3EA', // Light Blue/Grey-Blue for page background
      paper: '#ffffff',
    },
    text: {
      primary: '#2D4B73', // Dark Blue text for better readability
      secondary: '#2D4B73', // Dark Blue for secondary text
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#2D4B73',
    },
    h2: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h3: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h4: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h5: {
      fontWeight: 600,
      color: '#2D4B73',
    },
    h6: {
      fontWeight: 600,
      color: '#2D4B73',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
        },
        containedPrimary: {
          backgroundColor: '#2D4B73',
          color: '#ffffff', // Explicit white text
          border: '2px solid #1E3452',
          boxShadow: '0 4px 14px 0 rgba(45, 75, 115, 0.4)',
          '&:hover': {
            backgroundColor: '#1E3452',
            color: '#ffffff', // Maintain white text on hover
            boxShadow: '0 6px 20px 0 rgba(45, 75, 115, 0.5)',
          },
        },
        containedSecondary: {
          backgroundColor: '#2D4B73',
          color: '#ffffff', // Explicit white text
          border: '2px solid #1E3452',
          boxShadow: '0 4px 14px 0 rgba(45, 75, 115, 0.4)',
          '&:hover': {
            backgroundColor: '#1E3452',
            color: '#ffffff', // Maintain white text on hover
            boxShadow: '0 6px 20px 0 rgba(45, 75, 115, 0.5)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 12,
            border: '1px solid rgba(45, 75, 115, 0.3)',
            '&:hover': {
              border: '1px solid rgba(45, 75, 115, 0.5)',
            },
            '&.Mui-focused': {
              border: '1px solid #2D4B73',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#2D4B73',
            '&.Mui-focused': {
              color: '#2D4B73',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: '#2D4B73',
            '&::placeholder': {
              color: 'rgba(45, 75, 115, 0.6)',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px 0 rgba(139, 92, 246, 0.15)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D4B73', // Dark Blue header
          boxShadow: '0 4px 20px 0 rgba(45, 75, 115, 0.3)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          background: '#BFD3EA', // Light Blue/Grey-Blue background
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, provider } = useAuth();

  console.log('ProtectedRoute: isAuthenticated:', isAuthenticated);
  console.log('ProtectedRoute: isLoading:', isLoading);
  console.log('ProtectedRoute: provider:', provider);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    console.log('ProtectedRoute: Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('ProtectedRoute: Authenticated, rendering children');
  return <>{children}</>;
};

const Login: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      console.log('Login: Starting login process...');
      await login({ email, password });
      console.log('Login: Login successful, navigating to new entry...');
      navigate('/new-entry');
    } catch (err) {
      console.error('Login: Login failed:', err);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: 24,
          padding: 4,
          border: '2px solid rgba(45, 75, 115, 0.3)',
          boxShadow: '0 20px 40px rgba(45, 75, 115, 0.3)',
        }}
      >
        <Box
          component="img"
          src="/logo_black.png"
          alt="EasyIP Logo"
          sx={{
            maxWidth: '300px',
            width: '100%',
            height: 'auto',
            mb: 3,
          }}
        />
        <Typography component="h1" variant="h5">
          Provider
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ fontWeight: 600 }}>
              Forgot password?
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <Layout>
                <ClientsList />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-entry"
          element={
            <ProtectedRoute>
              <Layout>
                <NewEntry />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/new-visit" element={<Navigate to="/new-entry" replace />} />
        <Route
          path="/visits"
          element={
            <ProtectedRoute>
              <Layout>
                <VisitsList />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transportation"
          element={
            <ProtectedRoute>
              <Layout>
                <Transportation />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/unusual-incidents"
          element={
            <ProtectedRoute>
              <Layout>
                <UnusualIncidentsList />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Layout>
                <Billing />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/new-entry" replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SessionTimeoutNotification />
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
